import { type UiWidgetsLayoutType } from '@bett3r-dev/teselly-domain';
import type { WidgetDefinition } from './WidgetBuilder/types';

// TV1-1933 (S18) — `criticalStock` is the first NON-chart widget type: a tabular
// days-of-runway list (web only; a table doesn't fit the mobile grid).
// TV1-1929 (S14) — `table` is the generic tabular primitive. There is NO separate
// `pivot` type: in Cube a flat table and a pivot are the same query with a
// different `pivotConfig`, so pivoting is a widget PROP (see `TableWidget`).
// Web-only for the same reason as `criticalStock`.
// TV1-1930 (S15) — `kpi` is the big-number primitive. Mobile-CAPABLE: a single
// large number is the most mobile-friendly widget there is.
// TV1-1930 (S15) — `text` is the FIRST DATALESS widget type: a markdown block with
// NO cube, NO measures and NO query at all. It deliberately breaks the "cube + at
// least one measure" invariant every other widget assumes — see
// DATALESS_WIDGET_TYPES below. Web-only.
// TV1-2291 (SDD §5 · t21) — `heading` and `spacer` are the SECOND and THIRD
// dataless types. Web-only, for the same reason `text` is: a phone report is a
// single column, so a section rule and a deliberate gap have nothing to divide.
// Ticket 20 widened the DOMAIN union first (`UiWidgetHeadingSchema` /
// `UiWidgetSpacerSchema`); this is the front-end half, and widening it here is
// what makes the four EXHAUSTIVE `Record<WidgetType, …>` tables — `MIN_CONTENT_PX`,
// `WIDGET_CATALOG`, `widgetValidators`, `widgetValidatorsMobile`, plus
// `WidgetIcons` — refuse to compile until each has an entry. That is the
// mechanism working; add the entry, never loosen the table.
//
// TV1-2291 (SDD §6 · t25) — `count` / `alert` / `list` / `progress` are the four
// READMODEL-sourced types, and they land here the day their renderers do. Ticket
// 23 widened the DOMAIN union and ticket 24 taught the fetch chokepoint to route
// them, but the front-end union deliberately stayed narrow until now: a member of
// this union is a member of `WIDGET_CATALOG`, hence of `ALL_WIDGET_TYPES`, hence a
// palette button — and a palette button for a type `WebWidget` cannot draw adds a
// card that renders as `undefined`. Widening it here broke all five exhaustive
// tables at once, exactly as designed; every one of them was given an entry rather
// than a `Partial`.
//
// WEB-ONLY, all four. They are absent from `mobileWidgetTypes` for the reason
// `table` and `criticalStock` are: `list` is tabular, and `count` / `alert` /
// `progress` replace DESKTOP dashboard surfaces that the native mobile app renders
// through its own screens rather than through a report.
export type WidgetType = 'areaChart' | 'barChart' | 'lineChart' | 'pieChart' | 'donutChart' | 'scatterChart' | 'criticalStock' | 'table' | 'kpi' | 'text' | 'heading' | 'spacer' | 'count' | 'alert' | 'list' | 'progress';
export const webWidgetTypes: WidgetType[] = [ 'areaChart', 'barChart', 'lineChart', 'pieChart', 'donutChart', 'scatterChart', 'criticalStock', 'table', 'kpi', 'text', 'heading', 'spacer', 'count', 'alert', 'list', 'progress' ];
export const mobileWidgetTypes: WidgetType[] = [ 'areaChart', 'barChart', 'lineChart', 'pieChart', 'donutChart', 'kpi' ];
export function getDeviceWidgetTypes( availableTypes: WidgetType[], device: 'web' | 'mobile' | undefined ): WidgetType[] {
  if( !device ) return availableTypes;
  const allowed = device === 'web' ? webWidgetTypes : mobileWidgetTypes;
  return availableTypes.filter( t => allowed.includes( t ));
}

/**
 * Widget types that can ask for a period-over-period comparison
 * (`dateRange.compare` -> Cube's `compareDateRange`).
 *
 * TV1-1930 — deliberately narrow. A compare result CANNOT go through `rawData()`
 * at all (it throws — see `widgetData.extractWidgetData`); it arrives as a
 * `{ current, prior }` pair. Only the KPI parser understands that shape. Handing
 * it to a chart parser would give Mantine's charts a non-array `data` prop.
 *
 * The builder therefore only offers the compare toggle for these types (see
 * `Sections/DateRange/DateRange.tsx`). A hand- or LLM-authored definition that
 * sets `compare` on a chart degrades to an empty chart rather than crashing.
 */
export const COMPARE_CAPABLE_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>(['kpi']);

/**
 * Widget types that have NO data binding at all — no cube, no measures, no Cube
 * query, no fetch.
 *
 * TV1-1930 — `text` is the first. Every other widget in this app assumes "a cube
 * and at least one measure", and that assumption is hard-coded in exactly two
 * places on the front end:
 *
 *   1. `widgetDefinition.parseWidgetDefinition` — `cube` required, `measures`
 *      non-empty;
 *   2. `WebWidgetValidators.validateCommon` (and its Mobile twin).
 *
 * Both consult this set, and that is the WHOLE cost of the exception — two `if`s.
 * There is a third consumer: `WidgetLayoutComponent.getWidgetData` skips the Cube
 * round-trip entirely for these types (a dataless widget has no query to send;
 * `cubeApi.load({})` would error, get swallowed, and leave a permanent Skeleton).
 *
 * The DOMAIN side needed no exception at all: `measures` is already `.optional()`
 * there, and `cube` does not exist in the domain schema in the first place — the
 * Text schema simply extends `UiWidgetBaseSchema` rather than
 * `UiWidgetDataBaseSchema`.
 *
 * ⚠️ REJECTED: splitting `WidgetDefinition` into a data-bound/dataless union. It
 * would force narrowing at every one of ~13 threading sites, for a single type.
 *
 * TV1-2291 (t21) — `heading` and `spacer` join it, and the "whole cost is two
 * `if`s" claim above still holds: neither needed a third exception. Membership
 * here is ALSO what keeps them off Cube — `widgetData.isDatalessWidget` reads this
 * set, and `WidgetLayoutComponent.getWidgetData` returns `DATALESS_WIDGET_DATA`
 * before it ever builds a query. A type that renders without a query but is
 * missing from this set does not merely waste a round trip: `cubeApi.load({})`
 * errors, the error is caught, and the widget shows an error tile forever.
 */
export const DATALESS_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([ 'text', 'heading', 'spacer' ]);

/** `true` when the widget has no Cube query to run (see {@link DATALESS_WIDGET_TYPES}). */
export const isDatalessWidgetType = ( type: WidgetType | string | undefined ): boolean =>
  DATALESS_WIDGET_TYPES.has( type as WidgetType );

/**
 * TV1-2291 (§6) — widget types whose data comes from a PV3 READMODEL rather than
 * from Cube. The THIRD source, beside `analytics` and "no query at all".
 *
 * They are the discriminant of `UiWidgetReadmodelBaseSchema` (ticket 23): each of
 * the four domain schemas is that base plus a `type` const, so "is this a readmodel
 * widget" is answered by the type and nothing else — exactly as `DATALESS_WIDGET_TYPES`
 * answers it for `text`.
 *
 * ⚠️ Typed `ReadonlySet<WidgetType>` since ticket 25. Ticket 24 declared it as
 * `ReadonlySet<string>` because the front-end union did not contain these four yet —
 * a catalog entry / palette item for a type nothing can draw is a broken card, so the
 * renderers and the union widening had to land together. They have; the set is now
 * narrowed exactly as that note promised, and NOTHING else about it changed.
 *
 * Consumers, all of which mirror a `DATALESS_WIDGET_TYPES` consumer one-for-one:
 *
 *   1. `widgetData.isReadmodelWidget` -> `WidgetLayoutComponent.getWidgetData`'s third
 *      fetch branch (a readmodel widget has no cube and no measures, so
 *      `parseWidgetDefinition` would reject it and `cubeApi.load({})` would error);
 *   2. `widgetDefinitionAdapter` — the domain <-> builder carve-out that carries the
 *      readmodel binding (`collection` / `filter` / `limit`) across the seam. Without
 *      it the binding is dropped on the way IN and the fetch branch is unreachable;
 *   3. `WebWidgetValidators.validateCommon` — the cube/measures preconditions are
 *      skipped for these four, exactly as they are for a dataless type. A readmodel
 *      widget has no `cube` and no `measures` by construction, so demanding them
 *      would make every readmodel card permanently invalid (t25).
 */
export const READMODEL_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([
  'count',
  'alert',
  'list',
  'progress'
]);

/** `true` when the widget's data comes from a readmodel (see {@link READMODEL_WIDGET_TYPES}). */
export const isReadmodelWidgetType = ( type: WidgetType | string | undefined ): boolean =>
  typeof type === 'string' && READMODEL_WIDGET_TYPES.has( type as WidgetType );

/**
 * Widget types that draw their OWN tile and must not receive the layout's title
 * header (TV1-2291 · t21).
 *
 * `WidgetLayoutViewer` and `WidgetLayoutEditor` both render
 * `widget.definition.title` above the widget body. For these two that is wrong:
 *
 *  - `heading` — its `title` IS its content (there is deliberately no separate
 *    content field, in the schema or here), so the chrome header would print the
 *    section name TWICE, once in the app's body-text style and once as the
 *    heading proper;
 *  - `spacer` — must render nothing at all for a reader; a header would make a
 *    deliberate gap visibly not a gap.
 *
 * The prototype makes the same call (`prototypes/prototype-b-grid.html:1214`,
 * `showHead`). Kept as data next to the union rather than as a `type === 'heading'
 * || type === 'spacer'` in two renderers, because the two renderers drifting apart
 * is exactly how the title ends up doubled in one of them.
 */
export const CHROMELESS_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([ 'heading', 'spacer' ]);

/** `true` when the layout must NOT draw a title header for this type. */
export const isChromelessWidgetType = ( type: WidgetType | string | undefined ): boolean =>
  CHROMELESS_WIDGET_TYPES.has( type as WidgetType );


export type WidgetProps = {
    widget: Widget;
    data: any;
    type: WidgetType;
    widgetComponentProps: any;
    /**
     * TV1-2291 (t21) — `true` only when the report is being EDITED.
     *
     * `WidgetLayoutEditor` IS edit mode (`WidgetLayoutComponent` renders it or the
     * viewer, never both), so it passes `true` and the viewer passes nothing. The
     * default is therefore the reader-facing behaviour, which is the one you get by
     * forgetting — `spacer` is the only consumer today, and a spacer that leaks its
     * author-only placeholder into a report is a visible defect, whereas a spacer
     * that hides it in the editor is merely awkward.
     */
    editMode?: boolean;
};

// The layout-widget envelope (x/y/width/height + definition) is the domain
// source of truth; we only override `definition` with the concrete builder-form
// `WidgetDefinition`. (The old `Required<... extends (infer T)[] ? T : any>`
// derivation collapsed to `any` because `widgets` is optional; `NonNullable<>`
// + indexed access resolves it cleanly.)
type UiWidgetsLayoutWidget = NonNullable<UiWidgetsLayoutType['widgets']>[number];
export type Widget = Omit<UiWidgetsLayoutWidget, 'definition'> & {
  definition: WidgetDefinition;
};

// `WidgetDefinition` is the single source of truth for the front-end widget
// config (the builder form). It is re-exported here so validators, renderers and
// the layout editor share the one concrete grammar. The Cube wire-query shape it
// maps onto lives in `./widgetDefinition` (`widgetDefinitionToCubeQuery`).
export type { WidgetDefinition };
